import { asCategoryId, asItemId, asTodoId, between, daysBetween, deepEqual, newId } from "@pack-it-up/shared";
import { categoryKind } from "../entities/category.js";
import { contentEquals, detach, snapshotOf } from "../entities/provenance.js";
import { emptyCompletion } from "./completion.js";
import { dedupeKey, normalizeName } from "./importTemplates.js";
// Stable identity for an entry, so the ViewModel can hold ticks in a plain Set and
// the planner can read them back. Trip-side id when there is one (a trip row is in
// at most one entry), the template row otherwise.
export const syncEntryKey = (e) => e.tripRowId != null
    ? `${e.collection}:trip:${e.tripRowId}`
    : `${e.collection}:tpl:${e.templateRowId ?? ""}`;
// --- shared helpers -------------------------------------------------------
const catKey = (name, kind) => `${kind} ${normalizeName(name)}`;
const rowKey = (row) => dedupeKey(row.name, row.description, row.notes);
const sourceFor = (row, templateId) => row.provenance?.templateSources.find((s) => s.templateId === templateId)?.templateItemId;
const CHECKED_BY_DEFAULT = {
    unchanged: true,
    edited: true,
    conflict: false,
    new: false,
    templateOnly: true,
};
// Doc 13 §3 — three-way compare against the frozen import snapshot.
const classify = (tripRow, templateRow) => {
    const ours = snapshotOf(tripRow);
    const theirs = snapshotOf(templateRow);
    // A row matched by dedupe key has no snapshot for this template; treating the
    // template's current content as the base makes it a clean push (Doc 13 §2.1).
    const base = tripRow.provenance?.importedSnapshot ?? theirs;
    if (contentEquals(ours, theirs))
        return { status: "unchanged" };
    if (contentEquals(theirs, base))
        return { status: "edited" };
    // The template moved but the trip did not: nothing to push (resetItemToImport's
    // territory, Doc 06 §10).
    if (contentEquals(ours, base))
        return { status: "unchanged" };
    return { status: "conflict", templateValue: theirs };
};
// --- draft ----------------------------------------------------------------
export const computeTemplateSyncDraft = (templateId, _trip, tripContents, templateContents) => {
    const entries = [];
    // --- categories: match on (normalizeName, kind), identical to import ---
    const tplCatByKey = new Map();
    for (const c of templateContents.categories)
        tplCatByKey.set(catKey(c.name, categoryKind(c)), c);
    // trip category id → template category
    const catMatch = new Map();
    const claimedCats = new Set();
    for (const c of tripContents.categories) {
        const hit = tplCatByKey.get(catKey(c.name, categoryKind(c)));
        if (hit) {
            catMatch.set(c.id, hit);
            claimedCats.add(hit.id);
        }
        entries.push({
            collection: "categories",
            status: hit ? (hit.name === c.name ? "unchanged" : "edited") : "new",
            tripRowId: c.id,
            ...(hit ? { templateRowId: hit.id } : {}),
            defaultChecked: CHECKED_BY_DEFAULT[hit ? (hit.name === c.name ? "unchanged" : "edited") : "new"],
            assignmentSkipped: false,
        });
    }
    for (const c of templateContents.categories) {
        if (claimedCats.has(c.id))
            continue;
        entries.push({
            collection: "categories",
            status: "templateOnly",
            templateRowId: c.id,
            defaultChecked: CHECKED_BY_DEFAULT.templateOnly,
            assignmentSkipped: false,
        });
    }
    // --- rows ---
    const rowEntries = (collection, tripRows, templateRows) => {
        const tplById = new Map(templateRows.map((r) => [r.id, r]));
        const claimed = new Set();
        const matched = new Map(); // trip row id → template row
        // 1. by source id — authoritative, survives a rename on either side.
        const bySource = [];
        const rest = [];
        for (const row of tripRows) {
            const sourceId = sourceFor(row, templateId);
            if (sourceId === undefined) {
                rest.push(row);
                continue;
            }
            bySource.push(row);
            const hit = tplById.get(sourceId);
            // An unresolvable source id (the row was deleted from the template after
            // this trip imported it) makes the row `new`, never `unchanged` (Doc 13 §2.1).
            if (hit && !claimed.has(hit.id)) {
                claimed.add(hit.id);
                matched.set(row.id, hit);
            }
        }
        // 2. by dedupe key on an *unclaimed* template row in the matching category.
        const unclaimedByCatKey = new Map();
        for (const r of templateRows) {
            if (claimed.has(r.id))
                continue;
            let inner = unclaimedByCatKey.get(r.categoryId);
            if (inner == null) {
                inner = new Map();
                unclaimedByCatKey.set(r.categoryId, inner);
            }
            const key = rowKey(r);
            inner.set(key, [...(inner.get(key) ?? []), r]);
        }
        for (const row of rest) {
            const tplCat = catMatch.get(row.categoryId);
            if (!tplCat)
                continue;
            const bucket = unclaimedByCatKey.get(tplCat.id)?.get(rowKey(row));
            const hit = bucket?.find((r) => !claimed.has(r.id));
            if (hit) {
                claimed.add(hit.id);
                matched.set(row.id, hit);
            }
        }
        for (const row of [...bySource, ...rest]) {
            const hit = matched.get(row.id);
            const { status, templateValue } = hit
                ? classify(row, hit)
                : { status: "new", templateValue: undefined };
            entries.push({
                collection,
                status,
                tripRowId: row.id,
                ...(hit ? { templateRowId: hit.id } : {}),
                defaultChecked: CHECKED_BY_DEFAULT[status],
                ...(templateValue ? { templateValue } : {}),
                // Doc 13 §4.1 — a `specific` assignment names this trip's participants and
                // is never portable, so the row is flagged whether or not it is ticked.
                assignmentSkipped: row.assignment.kind === "specific",
            });
        }
        for (const r of templateRows) {
            if (claimed.has(r.id))
                continue;
            entries.push({
                collection,
                status: "templateOnly",
                templateRowId: r.id,
                defaultChecked: CHECKED_BY_DEFAULT.templateOnly,
                assignmentSkipped: false,
            });
        }
    };
    rowEntries("items", tripContents.items, templateContents.items);
    rowEntries("todos", tripContents.todos, templateContents.todos);
    return { templateId, entries };
};
// --- plan -----------------------------------------------------------------
// Doc 13 §4.2 — template todos carry a relative offset, trip todos an absolute date.
// undefined = don't touch the field; null = clear it.
const offsetFor = (todo, trip) => {
    if (!todo.dueDateManuallyOverridden)
        return todo.relativeDueOffset ?? null;
    if (trip.startDate == null || todo.dueDate == null)
        return undefined; // nothing derivable
    return { value: daysBetween(trip.startDate, todo.dueDate), unit: "day" };
};
// Doc 13 §4.1 — `specific` is skipped; a template row created by a sync gets `everyone`.
const portableAssignment = (a) => a.kind === "specific" ? undefined : a;
// Doc 13 §5 — categories are matched by the draft; a trip category with no match is
// created lazily, only if a row actually lands in it. Renames ride on ticked entries.
const makeCategoryResolver = (draft, templateId, tripCats, tplCats, templateCategories, checked, idGen) => {
    const creates = [];
    const renames = [];
    const created = new Set();
    const matched = new Map(); // trip category id → template category id
    for (const e of draft.entries) {
        if (e.collection !== "categories" || e.tripRowId == null || e.templateRowId == null)
            continue;
        matched.set(e.tripRowId, asCategoryId(e.templateRowId));
        const tripCat = tripCats.get(e.tripRowId);
        const tplCat = tplCats.get(e.templateRowId);
        if (e.status === "edited" && tripCat && tplCat && checked.has(syncEntryKey(e)))
            renames.push({ id: tplCat.id, name: tripCat.name });
    }
    let cursor = null;
    for (const c of templateCategories)
        if (cursor === null || c.order > cursor)
            cursor = c.order;
    const resolve = (tripCategoryId, kind) => {
        const existing = matched.get(tripCategoryId);
        if (existing) {
            created.add(existing);
            return existing;
        }
        const source = tripCats.get(tripCategoryId);
        const id = asCategoryId(idGen());
        const order = between(cursor, null);
        cursor = order;
        creates.push({
            id,
            parentId: templateId,
            name: source?.name ?? "Uncategorised",
            order,
            collapsedBy: {},
            kind,
        });
        matched.set(tripCategoryId, id);
        created.add(id);
        return id;
    };
    return { creates, renames, resolve, created };
};
// Order is never synced (Doc 13 §4) — new rows are appended, so all this needs is a
// per-category high-water mark seeded from the template's current maximum.
const makeOrderCursors = (rows) => {
    const max = new Map();
    for (const r of rows) {
        const cur = max.get(r.categoryId);
        if (cur === undefined || r.order > cur)
            max.set(r.categoryId, r.order);
    }
    return (categoryId) => {
        const next = between(max.get(categoryId) ?? null, null);
        max.set(categoryId, next);
        return next;
    };
};
const collectionOf = (row) => "quantity" in row ? "items" : "todos";
// Doc 13 §6 — the trip-side half of a sync.
//
// `rebase` is what makes the feature idempotent. Once a trip row has been pushed into
// the template, the trip row's provenance is re-pointed at the template row it now
// corresponds to (creating that source entry if the row was `new`), and the frozen
// `importedSnapshot` is re-taken from the trip row itself. That snapshot is the base
// `classify` compares against, so the very next `computeTemplateSyncDraft` sees
// ours == base == theirs and reports `unchanged` with nothing to write — the §1.9
// property test. Re-taking it from the *trip* row rather than the template row is
// deliberate: it keeps a row whose `specific` assignment was skipped from being
// flagged as permanently edited (Doc 13 §4.1).
//
// `createdByImport` is preserved, never set: a row the user typed by hand and then
// pushed into the template must not become deletable by that template's delete.
// The `deepEqual` guard suppresses a no-op write so a second sync is genuinely empty.
const makeProvenancePlanner = (templateId) => {
    const patches = [];
    const rebase = (row, templateRowId) => {
        const current = row.provenance;
        const sources = current ? [...current.templateSources] : [];
        const idx = sources.findIndex((s) => s.templateId === templateId);
        const source = { templateId, templateItemId: templateRowId };
        if (idx >= 0)
            sources[idx] = source;
        else
            sources.push(source);
        const templateIds = [...(row.templateIds ?? [])];
        if (!templateIds.includes(templateId))
            templateIds.push(templateId);
        const provenance = {
            templateSources: sources,
            createdByImport: current?.createdByImport ?? false,
            importedSnapshot: snapshotOf(row),
            modifiedSinceImport: false,
        };
        if (current != null &&
            deepEqual(current, provenance) &&
            deepEqual(row.templateIds ?? [], templateIds))
            return;
        patches.push({ collection: collectionOf(row), id: row.id, provenance, templateIds });
    };
    // The row was removed from the template, so the trip row stops claiming it as a
    // source — the same `detach` a template delete uses (Doc 07 §5).
    const detachFrom = (row) => {
        if (row.provenance == null)
            return;
        const detached = detach(row.provenance, row.templateIds, templateId);
        if (deepEqual(detached.provenance, row.provenance) &&
            deepEqual(detached.templateIds, row.templateIds ?? []))
            return;
        patches.push({
            collection: collectionOf(row),
            id: row.id,
            provenance: detached.provenance,
            templateIds: detached.templateIds,
        });
    };
    return { patches, rebase, detachFrom };
};
// --- template row construction (Doc 13 §4) --------------------------------
const buildTemplateItem = (src, categoryId, order, idGen) => ({
    id: asItemId(idGen()),
    categoryId,
    name: src.name,
    quantity: src.quantity,
    ...(src.description !== undefined ? { description: src.description } : {}),
    ...(src.notes !== undefined ? { notes: src.notes } : {}),
    ...(src.tags !== undefined ? { tags: src.tags } : {}),
    assignment: portableAssignment(src.assignment) ?? { kind: "everyone" },
    completions: emptyCompletion(),
    order,
});
const buildTemplateTodo = (src, trip, categoryId, order, idGen) => {
    const offset = offsetFor(src, trip);
    return {
        id: asTodoId(idGen()),
        categoryId,
        name: src.name,
        ...(src.description !== undefined ? { description: src.description } : {}),
        ...(src.notes !== undefined ? { notes: src.notes } : {}),
        ...(src.tags !== undefined ? { tags: src.tags } : {}),
        assignment: portableAssignment(src.assignment) ?? { kind: "everyone" },
        completions: emptyCompletion(),
        ...(offset ? { relativeDueOffset: offset } : {}),
        dueDateManuallyOverridden: false,
        remindersSentTo: {},
        reminderComplete: false,
        order,
    };
};
// `completions`, `staging` and `provenance` are never written to a template row by an
// *update* — they are instance state. `tags` are content (T29), so they sync back like
// the name does; `null` clears a template row's tags when the trip row has none.
const itemPatchFor = (src) => {
    const assignment = portableAssignment(src.assignment);
    return {
        name: src.name,
        quantity: src.quantity,
        description: src.description ?? null,
        notes: src.notes ?? null,
        tags: src.tags ?? null,
        ...(assignment ? { assignment } : {}),
    };
};
const todoPatchFor = (src, trip) => {
    const assignment = portableAssignment(src.assignment);
    const offset = offsetFor(src, trip);
    return {
        name: src.name,
        description: src.description ?? null,
        notes: src.notes ?? null,
        tags: src.tags ?? null,
        ...(assignment ? { assignment } : {}),
        ...(offset !== undefined ? { relativeDueOffset: offset } : {}),
    };
};
// Doc 13 §5 — the category delete guard: an unticked template category goes only if
// every row inside it is going too, and nothing new landed in it.
const planCategoryDeletes = (draft, tplCats, templateContents, deletedTemplateRows, createdInCategory, checked) => {
    const survivors = new Map();
    for (const r of [...templateContents.items, ...templateContents.todos]) {
        if (deletedTemplateRows.has(r.id))
            continue;
        survivors.set(r.categoryId, (survivors.get(r.categoryId) ?? 0) + 1);
    }
    const deletes = [];
    for (const e of draft.entries) {
        if (e.collection !== "categories" || e.templateRowId == null)
            continue;
        if (checked.has(syncEntryKey(e)))
            continue;
        const tplCat = tplCats.get(e.templateRowId);
        if (!tplCat)
            continue;
        if ((survivors.get(tplCat.id) ?? 0) > 0)
            continue;
        if (createdInCategory.has(tplCat.id))
            continue;
        deletes.push(tplCat.id);
    }
    return deletes;
};
export const planTemplateSync = (draft, trip, tripContents, templateContents, checked, idGen = newId) => {
    const { templateId } = draft;
    const tripCats = new Map(tripContents.categories.map((c) => [c.id, c]));
    const tripItems = new Map(tripContents.items.map((r) => [r.id, r]));
    const tripTodos = new Map(tripContents.todos.map((r) => [r.id, r]));
    const tplCats = new Map(templateContents.categories.map((c) => [c.id, c]));
    const tplItems = new Map(templateContents.items.map((r) => [r.id, r]));
    const tplTodos = new Map(templateContents.todos.map((r) => [r.id, r]));
    const categories = makeCategoryResolver(draft, templateId, tripCats, tplCats, templateContents.categories, checked, idGen);
    const nextItemOrder = makeOrderCursors(templateContents.items);
    const nextTodoOrder = makeOrderCursors(templateContents.todos);
    const provenance = makeProvenancePlanner(templateId);
    const itemCreates = [];
    const itemUpdates = [];
    const itemDeletes = [];
    const todoCreates = [];
    const todoUpdates = [];
    const todoDeletes = [];
    // Template rows removed by this sync — the category delete guard reads this.
    const deletedTemplateRows = new Set();
    for (const e of draft.entries) {
        if (e.collection === "categories")
            continue;
        const isChecked = checked.has(syncEntryKey(e));
        const isItem = e.collection === "items";
        const tripRow = e.tripRowId == null ? undefined : isItem ? tripItems.get(e.tripRowId) : tripTodos.get(e.tripRowId);
        const tplRow = e.templateRowId == null ? undefined : isItem ? tplItems.get(e.templateRowId) : tplTodos.get(e.templateRowId);
        if (!isChecked) {
            // Mirror semantics, bounded by the default: unticking a *pre-ticked* row (or an
            // "only in template" row) removes it from the template. A conflict/new row was
            // never ticked, so leaving it alone is a no-op (Doc 13 §1, §2).
            if (!e.defaultChecked || !tplRow)
                continue;
            deletedTemplateRows.add(tplRow.id);
            if (isItem)
                itemDeletes.push(tplRow.id);
            else
                todoDeletes.push(tplRow.id);
            if (tripRow)
                provenance.detachFrom(tripRow);
            continue;
        }
        if (e.status === "templateOnly")
            continue; // ticked = keep as-is, no write
        if (!tripRow)
            continue;
        let templateRowId;
        if (e.status === "new") {
            const categoryId = categories.resolve(tripRow.categoryId, isItem ? "packing" : "todo");
            if (isItem) {
                const created = buildTemplateItem(tripRow, categoryId, nextItemOrder(categoryId), idGen);
                itemCreates.push(created);
                templateRowId = created.id;
            }
            else {
                const created = buildTemplateTodo(tripRow, trip, categoryId, nextTodoOrder(categoryId), idGen);
                todoCreates.push(created);
                templateRowId = created.id;
            }
        }
        else if (tplRow) {
            templateRowId = tplRow.id;
            if (e.status === "edited" || e.status === "conflict") {
                if (isItem)
                    itemUpdates.push({ id: tplRow.id, patch: itemPatchFor(tripRow) });
                else
                    todoUpdates.push({
                        id: tplRow.id,
                        patch: todoPatchFor(tripRow, trip),
                    });
            }
        }
        if (templateRowId !== undefined)
            provenance.rebase(tripRow, templateRowId);
    }
    return {
        templateId,
        categoryCreates: categories.creates,
        categoryRenames: categories.renames,
        categoryDeletes: planCategoryDeletes(draft, tplCats, templateContents, deletedTemplateRows, categories.created, checked),
        itemCreates,
        itemUpdates,
        itemDeletes,
        todoCreates,
        todoUpdates,
        todoDeletes,
        tripProvenancePatches: provenance.patches,
    };
};
// Counts for the mode bar and the confirm dialog — "12 to update · 3 to add ·
// 1 to remove · 2 conflicts". Rows only; categories follow their rows.
export const syncCounts = (draft, checked) => {
    let updates = 0;
    let adds = 0;
    let removes = 0;
    let conflicts = 0;
    for (const e of draft.entries) {
        if (e.collection === "categories")
            continue;
        const isChecked = checked.has(syncEntryKey(e));
        if (e.status === "conflict")
            conflicts += 1;
        if (isChecked) {
            if (e.status === "new")
                adds += 1;
            else if (e.status === "edited" || e.status === "conflict")
                updates += 1;
        }
        else if (e.defaultChecked && e.templateRowId != null) {
            removes += 1;
        }
    }
    return { updates, adds, removes, conflicts };
};
//# sourceMappingURL=syncToTemplate.js.map